import { useMemo } from 'react';
import { NoShowRateByType } from '@weave/schema-gen-ts/dist/schemas/analytics-reporting/analytics_gateway/analytics_gateway.pb';
import { capitalize } from 'lodash-es';
import { AnalyticsHooks, ChartCTA, DemoChip, InfoTipPopover } from '@frontend/analytics-shared';
import { CategoryBarChartData, Chart } from '@frontend/charts';
import { useTranslation } from '@frontend/i18n';
import { formatters } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { useFetchNSROILocationsNoShowRate } from '../hooks/use-fetch-nsr-locations-no-show-rate';
import { useNSRRoiShallowStore } from '../hooks/use-nsr-roi-store';
import { CommonChartProps } from './types';

type ProcessedChartData = {
  chartData: CategoryBarChartData;
  colors: Record<string, string>;
  maxValues: Record<string, number>;
};

export const NSRRoiLocationsComparisonAppointmentType = ({ isDemoAccount }: CommonChartProps) => {
  const { t } = useTranslation('analytics');
  const { filters, setFilters } = useNSRRoiShallowStore('filters', 'setFilters');
  const { locationNames } = AnalyticsHooks.useAnalyticsOrgLocations({
    module: 'ROI',
  });

  const { data, isLoading } = useFetchNSROILocationsNoShowRate({
    noShowRateType: NoShowRateByType.APPOINTMENTS_EXTERNAL_TYPE,
    filters,
  });

  const { chartData, colors, maxValues } = useMemo<ProcessedChartData>(() => {
    // Get the list of all appointment types across dataset to fill missing values with 0 for each location
    const allAppointmentTypes = Array.from(
      new Set(data?.data?.locations?.flatMap((loc) => loc?.aggregates?.map(({ name }) => capitalize(name))) || [])
    ).sort();

    return {
      chartData:
        data?.data?.locations?.reduce<CategoryBarChartData>((acc, { aggregates = [], locationId = '' }) => {
          // Create a temporary map for quick lookup of values from aggregates
          const aggregateValueMap = aggregates.reduce<Record<string, number>>((mapAcc, { name = '', value = 0 }) => {
            mapAcc[capitalize(name)] = value;
            return mapAcc;
          }, {});

          const locationData: Record<string, number> = {};
          allAppointmentTypes.forEach((chartKey) => {
            if (chartKey) {
              locationData[chartKey] = aggregateValueMap[chartKey] || 0;
            }
          });

          acc[locationId] = locationData;
          return acc;
        }, {}) || {},
      ...allAppointmentTypes.reduce<{
        maxValues: Record<string, number>;
        colors: Record<string, string>;
      }>(
        (acc, chartKey) => {
          if (chartKey) {
            acc.colors[chartKey] = theme.colors.secondary.seaweed40;
            acc.maxValues[chartKey] = 100;
          }
          return acc;
        },
        {
          colors: {},
          maxValues: {},
        }
      ),
    };
  }, [data?.data?.locations]);

  const { highlight, description } = useMemo(() => {
    const allLocationData = Object.values(chartData);
    const locationCount = allLocationData.length;

    if (!locationCount) {
      return {
        highlight: '',
        description: '',
      };
    }

    const sums: Record<string, number> = {};
    allLocationData.forEach((locationObject) => {
      Object.entries(locationObject).forEach(([apptType, value]) => {
        sums[apptType] = (sums[apptType] || 0) + value;
      });
    });

    const [highestType] = Object.entries(sums).reduce(
      (maxEntry, currentEntry) => {
        return currentEntry[1] > maxEntry[1] ? currentEntry : maxEntry;
      },
      ['', -1] // Default: ['', -1] assumes all sums are 0 or positive
    );

    if (highestType === '') {
      return {
        highlight: '',
        description: '',
      };
    }

    return {
      highlight: highestType,
      description: t(
        'Identify high no-show types like {{highlight}} and optimize reminders filtered by appointment type',
        {
          highlight: highestType,
        }
      ),
    };
  }, [chartData, t]);

  return (
    <Chart
      colors={colors}
      isLoading={isLoading}
      labels={{
        ...locationNames,
        groupNameHeader: t('Location'),
      }}
      style={{ marginBottom: 0 }}
    >
      <Chart.Header
        title={t('No-Show Rate by Appointment Type')}
        infoTip={
          <InfoTipPopover>
            {t(
              'Track no-show rates by appointment type across locations to identify which services or visit types have higher no-show risks, enabling practices to tailor reminders and improve scheduling strategies'
            )}
          </InfoTipPopover>
        }
        leftElement={isDemoAccount && <DemoChip />}
      />
      <Chart.CategoryBarChart
        appearance={{
          alternateCategoryBackgroundColor: theme.colors.neutral5,
          barSize: 32,
          formatters: formatters.appendPercent,
          labelConfig: {
            customStyles: () => ({
              dy: 4,
              fill: theme.font.colors.default,
              fontSize: theme.font.size.medium,
              fontWeight: theme.font.weight.bold,
            }),
            distanceFromBar: 16,
            placement: 'end',
            show: true,
          },
          margin: { right: 68 },
          maxValues,
          showXAxis: false,
        }}
        data={chartData}
        onClick={({ group }) => {
          setFilters({
            locationIds: [group],
          });
        }}
      />
      <ChartCTA
        buttonLabel={t('Send Reminder')}
        ctaType='sendReminders'
        title={t('Overall Highest No-Show')}
        highlight={highlight}
        description={description}
      />
    </Chart>
  );
};
